// frontend/src/components/common/riskLevels.js
//
// The one frontend home for the stockout risk vocabulary: which levels exist,
// how urgent each is, and which colour its chip wears.
//
// Kept out of RiskChip.jsx so that file only exports the React component
// (react-refresh/only-export-components — HMR boundary).
//
// The chain runs Python → config → here, with every link pinned:
//   * api.views.report_helpers.RISK_LEVELS is what
//     reporting.ingredients.risk_priority_expr returns
//     (reporting/test_ingredients.py pins the tuple to the expression);
//   * scripts/audit-i18n.config.json declares the copy for those levels under
//     `reports.stockout.${riskLabelKey(level)}` (backend
//     reporting/test_i18n_dynamic_key_contracts.py pins config to Python);
//   * riskLevels.test.js pins RISK_LEVELS below to that same config entry.
// So a level added on either side surfaces as a failing test, not as a raw key
// path or a silently unsortable row.

/** Every level a row's `risk_level` can hold, most urgent first. */
export const RISK_LEVELS = [
  'out_of_stock',
  'below_minimum_stock',
  'at_risk',
  'no_demand_data',
  'sufficient',
];

/**
 * The i18n key for a level, relative to the `reports.stockout` namespace.
 *
 * The `riskLevels.` prefix is part of the key, not decoration: it is what the
 * audit config enumerates, so the two cannot be spelled differently.
 */
export const riskLabelKey = (level) => `riskLevels.${level}`;

/**
 * Sort priority — lower is more urgent.
 *
 * Derived from RISK_LEVELS' order rather than written out, so the order a
 * reader sees above IS the order rows sort in. Mirrors the identical derivation
 * behind report_helpers.RISK_PRIORITY.
 */
export const RISK_SORT_PRIORITY = Object.fromEntries(
  RISK_LEVELS.map((level, index) => [level, index]),
);

/**
 * AG Grid comparator over `risk_level` cells — the one order every grid that
 * shows the chip sorts by (Projected Stockout grid, Home at-risk card). A
 * level with no rank sorts last, however urgent it may be; riskLevels.test.js
 * pins that no declared level is unranked.
 */
export const compareRiskLevels = (a, b) =>
  (RISK_SORT_PRIORITY[a] ?? 99) - (RISK_SORT_PRIORITY[b] ?? 99);

/**
 * Per-level chip colour. Not derivable — an editorial choice per level — but
 * its key set is pinned to RISK_LEVELS by riskLevels.test.js, so a new level
 * cannot reach the grid uncoloured.
 */
export const RISK_CHIP_COLOR = {
  out_of_stock: 'error',
  below_minimum_stock: 'warning',
  at_risk: 'info',
  no_demand_data: 'neutral',
  sufficient: 'success',
};
